import { intersection, isEmpty } from 'lodash/fp';
import isNil from 'lodash/fp/isNil';
import { useEffect, useSyncExternalStore } from 'react';
import * as yup from 'yup';
import type { SubAccountType } from 'components/portfolio/account/Account.types';
import type { IPortfolioFiltersInputQuery } from 'generated/graphql';
import isEqual from 'lodash/fp/isEqual';
import { emptyToUndefined } from 'components/filter.utils';

type Listener = () => void;
const listeners = new Set<Listener>();

export const SUB_ACCOUNT_ASSET_FILTERS_KEY = 'subAccountAssetFilters';
export type SubFund = IPortfolioFiltersInputQuery['portfolio']['subFunds']['list'][number];
export type SubAccount = IPortfolioFiltersInputQuery['portfolio']['accounts'][number]['subAccounts'][number];

export type PortfolioFilters = {
  subFundIds: number[] | undefined;
  accountIds: string[] | undefined;
  subAccountIds: string[] | undefined;
  assetIds: string[] | undefined;
};

export const portfolioFiltersSchema = yup.object().shape({
  subFundIds: yup.array().of(yup.number()),
  accountIds: yup.array().of(yup.string()),
  subAccountIds: yup.array().of(yup.string()),
  assetIds: yup.array().of(yup.string()),
});

type UsePortfolioFilters = {
  portfolioFilters: PortfolioFilters;
  isFiltered: boolean;
  setPortfolioFilters: (newFilterValues: PortfolioFilters) => void;
};

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function setPortfolioFilterToExternalStorage(newFilterValues: PortfolioFilters): void {
  sessionStorage.setItem(
    SUB_ACCOUNT_ASSET_FILTERS_KEY,
    JSON.stringify({
      subFundIds: emptyToUndefined(newFilterValues.subFundIds),
      subAccountIds: emptyToUndefined(newFilterValues.subAccountIds),
      accountIds: emptyToUndefined(newFilterValues.accountIds),
      assetIds: emptyToUndefined(newFilterValues.assetIds),
    })
  );

  for (const listener of listeners) {
    listener();
  }
}

const previousState: {
  storageText: string;
  value: PortfolioFilters;
} = {
  storageText: '{}',
  value: { subFundIds: undefined, subAccountIds: undefined, assetIds: undefined, accountIds: undefined },
};

function getPortfolioFilterValues(): PortfolioFilters {
  const filterValueJson = sessionStorage.getItem(SUB_ACCOUNT_ASSET_FILTERS_KEY) ?? '{}';
  if (previousState.storageText === filterValueJson) {
    return previousState.value;
  }

  let filterValue: PortfolioFilters | undefined;
  try {
    filterValue = JSON.parse(filterValueJson);
    portfolioFiltersSchema.validateSync(filterValue);
  } catch (e) {
    console.warn(
      `filter value in session storage ${SUB_ACCOUNT_ASSET_FILTERS_KEY} should be {subFundIds: number[];subAccountIds: string[], assetIds: string[]}, but found ${filterValueJson}`,
      e
    );
    filterValue = undefined;
  }

  filterValue ??= { subFundIds: undefined, subAccountIds: undefined, assetIds: undefined, accountIds: undefined };
  previousState.storageText = filterValueJson;
  previousState.value = filterValue;
  return filterValue;
}

/** Store filter value in session storage to keep it between pages refreshes */
export function usePortfolioFilters(): UsePortfolioFilters {
  const filters = useSyncExternalStore(subscribe, () => getPortfolioFilterValues());

  return {
    portfolioFilters: filters,
    isFiltered:
      !isNil(filters.subAccountIds) ||
      !isNil(filters.subFundIds) ||
      !isNil(filters.assetIds) ||
      !isNil(filters.accountIds),
    setPortfolioFilters: setPortfolioFilterToExternalStorage,
  };
}

/** We need update filter value in sessionStorage in case some saved in filter subFundIds were removed */
export function useSyncPortfolioFilterWithCurrentData(queryData: {
  portfolio: {
    subFunds: {
      list: {
        id: number;
      }[];
    };
    accounts: {
      id: string;
      subAccounts: {
        id: string;
      }[];
    }[];
  };
  assets?: {
    list: {
      id: string;
    }[];
  };
}): void {
  const { setPortfolioFilters, portfolioFilters } = usePortfolioFilters();
  useEffect(() => {
    const subFundIds = intersection(
      portfolioFilters.subFundIds,
      queryData.portfolio.subFunds.list.map((subFund) => subFund.id)
    );

    const subAccountIds = intersection(
      queryData.portfolio.accounts.flatMap((account) => account.subAccounts).map((subAccount) => subAccount.id),
      portfolioFilters.subAccountIds
    );

    const accountIds = intersection(
      queryData.portfolio.accounts.map((account) => account.id),
      portfolioFilters.accountIds
    );

    const assetIds = intersection(
      (queryData.assets?.list ?? []).map((asset) => asset.id),
      portfolioFilters.assetIds
    );

    const newFilter = {
      subFundIds,
      subAccountIds,
      accountIds,
      assetIds,
    };

    // empty lists are the same as undefined in filters, so we need to compare values manually
    if (
      !isEqual(
        {
          subFundIds: portfolioFilters.subFundIds ?? [],
          subAccountIds: portfolioFilters.subAccountIds ?? [],
          accountIds: portfolioFilters.accountIds ?? [],
          assetIds: portfolioFilters.assetIds ?? [],
        },
        newFilter
      )
    ) {
      setPortfolioFilters(newFilter);
    }
  }, [portfolioFilters, setPortfolioFilters, queryData]);
}

export function useSyncPortfolioFilterWithCurrentSubFunds(subFunds: SubFund[] | undefined): void {
  const { setPortfolioFilters, portfolioFilters } = usePortfolioFilters();
  useEffect(() => {
    if (isNil(subFunds) || isEmpty(portfolioFilters.subFundIds)) {
      return;
    }
    const subFundIds = intersection(
      portfolioFilters.subFundIds,
      subFunds.map((subFund) => subFund.id)
    );

    setPortfolioFilters({
      ...portfolioFilters,
      subFundIds,
    });
  }, [portfolioFilters, setPortfolioFilters, subFunds]);
}

export function useSyncPortfolioFilterWithCurrentSubAccounts(subAccounts: SubAccountType[] | undefined): void {
  const { setPortfolioFilters, portfolioFilters } = usePortfolioFilters();
  useEffect(() => {
    if (isNil(subAccounts) || isEmpty(portfolioFilters.subAccountIds)) {
      return;
    }
    const subAccountIds = intersection(
      portfolioFilters.subAccountIds,
      subAccounts.map((subAccount) => subAccount.id)
    );

    setPortfolioFilters({
      ...portfolioFilters,
      subAccountIds,
    });
  }, [portfolioFilters, setPortfolioFilters, subAccounts]);
}
